// hooks/useChatInstance.js

import { useState, useEffect, useCallback } from "react";
import { dbPromise } from "../db/fsDB";

const MESSAGE_STORE = "chat-messages";

export function useChatInstances({
  activeChatKey,
  selectedItem,
  chatMetaReady,
  currentUser,
  users,
  groups,
}) {
  const [chatInstances, setChatInstances] = useState({});

  /**
   * Read every unique chatKey stored in IndexedDB.
   */
  const getAllChatKeys = useCallback(async () => {
    const db = await dbPromise;

    const tx = db.transaction(MESSAGE_STORE, "readonly");
    const store = tx.objectStore(MESSAGE_STORE);

    const keys = new Set();

    let cursor = await store.openCursor();

    while (cursor) {
      if (cursor.value?.chatKey) {
        keys.add(cursor.value.chatKey);
      }
      cursor = await cursor.continue();
    }

    await tx.done;

    return [...keys];
  }, []);

  /**
   * Register a live chat instance.
   */
 const registerChatInstance = useCallback((chatKey, item) => {
  if (!chatKey || !item) return;

  setChatInstances((prev) => ({
    ...prev,
    [chatKey]: {
      ...prev[chatKey],
      key: chatKey,
      item,
      mountedAt: prev[chatKey]?.mountedAt ?? Date.now(),
    },
  }));
}, []);

  /**
   * Keep the currently opened chat alive.
   */
  useEffect(() => {
    if (!activeChatKey || !selectedItem) return;
    registerChatInstance(activeChatKey, selectedItem);
  }, [activeChatKey, selectedItem, registerChatInstance]);

  /**
   * Hydrate live chat instances after login.
   */
  useEffect(() => {
    if (!chatMetaReady || !currentUser) return;

    let cancelled = false;

    (async () => {
      const chatKeys = await getAllChatKeys();

      if (cancelled) return;

      const hydrated = {};

      for (const key of chatKeys) {
        if (key.startsWith("user-")) {
          const chatId = key.slice(5);

          const user = users.find(
            (u) => String(u.chatId) === String(chatId)
          );

          if (user) {
            hydrated[key] = {
              key,
              item: {
                type: "user",
                chatId: user.chatId,
                displayName: user.displayName,
                username: user.userName || user.username,
                profileImageUrl: user.profileImageUrl,
                online: user.online,
                lastSeenAt: user.lastSeenAt,
              },
              mountedAt: Date.now(),
            };
          }
        }

        if (key.startsWith("group-")) {
          const groupId = key.slice(6);

          const group = groups.find(
            (g) => String(g.groupId) === String(groupId)
          );

          if (group) {
            hydrated[key] = {
              key,
              item: {
                type: "group",
                groupId: group.groupId,
                name: group.name,
                groupImageUrl: group.groupImageUrl,
              },
              mountedAt: Date.now(),
            };
          }
        }
      }

      setChatInstances((prev) => ({
        ...hydrated,
        ...prev, // Preserve already-mounted live instances.
      }));
    })();

    return () => {
      cancelled = true;
    };
  }, [
    chatMetaReady,
    currentUser,
    users,
    groups,
    getAllChatKeys,
  ]);

  return {
    chatInstances,
    registerChatInstance,
    setChatInstances,
  };
}